import React, {ChangeEvent, useEffect} from "react";
import {getContainer} from "../../ioc/IOCSetup";
import {connect} from "react-redux";
import {AppState} from "../../RootReducer";
import {Dispatch} from "redux";
import {UserInfo} from "../../http/protocol";
import './styles/NotificationsComponent.scss';
import {t} from "../../i18n";

interface Props {
    user?:UserInfo;
    pushNotificationsEnabled?: boolean;
}

export const NotificationsComponent = (props: Props) => {

    const [publicKey, setPublicKey] = React.useState("");
    const [blockedByChrome, setBlockedByChrome] = React.useState(false);
    const [isDeviceSubscribed, setIsDeviceSubscribed] = React.useState(false);
    const [devicePermission, setDevicePermission] = React.useState<"denied" | "granted" | "prompt" | "default">("default");

    /**
     * prompt user for push notification permission, then subscribe user if permission is granted
     */
    const handlePermissionPrompt = async () => {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                console.log("Permission granted!");
                subscribeUser();
            } else {
                console.log("Permission not granted.");
            }
            setDevicePermission(permission);
        } catch (error) {
            console.error("Error requesting notification permission:", error);
        }
    }

    const urlB64ToUint8Array = (base64String: string) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    const subscribeUser = async () => {
        try {
            const swRegistration: ServiceWorkerRegistration = await navigator.serviceWorker.ready;
            const pushSubscription = await swRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlB64ToUint8Array(publicKey)
            });

            const key = pushSubscription.getKey('p256dh');
            const auth = pushSubscription.getKey('auth');

            const sub = {
                endpoint: pushSubscription.endpoint,
                expirationTime: pushSubscription.expirationTime || 0,
                key: key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key) as any)) : '',
                auth: auth ? btoa(String.fromCharCode.apply(null, new Uint8Array(auth) as any)) : '',
            };

            // Send subscription to application server
            await getContainer().getNotificationsService().subscribeTo(sub);

            // Set the state to subscribed
            setIsDeviceSubscribed(true);
        } catch (error) {
            console.error('Error subscribing user:', error);
        }
    }

    const unSubscribeUser = async () => {
        console.log("Unsubscribing user...")
        try {
            // Wait until the service worker is ready
            const swRegistration = await navigator.serviceWorker.ready
            const subscription = await swRegistration.pushManager.getSubscription();

            const sub = {
                endpoint: subscription?.endpoint || '',
                expirationTime: subscription?.expirationTime || 0,
                key: '',
                auth: '',
            }

            if (subscription) {
                await getContainer().getNotificationsService().unSubscribeTo(sub);
                console.log("User unsubscribed successfully.");
                setIsDeviceSubscribed(false)
            } else {
                console.log("No active subscription found.");
            }
        } catch (error) {
            console.error("Error during unsubscription:", error);
        }
    };

    const handleOnChange = async () => {
        if (isDeviceSubscribed) {
            // Unsubscribe user
            unSubscribeUser();
        } else {
            // device is not subscribed
            if (devicePermission === "granted") {
                await subscribeUser();
            } else if (devicePermission === "prompt" || devicePermission === "default") {
                // ask for permission and then subscribe
                handlePermissionPrompt()
            } else {
                // Permission is not granted
                console.log("Push notifications permission denied by device");
            }
        }
    };

    const fetchAndSetPublicKey = async () => {
        try {
            let publicKey;

            const localStorageKey = localStorage.getItem("notificationKey");
            if (localStorageKey && localStorageKey.length > 0) {
                publicKey = localStorageKey;
            } else {
                publicKey = await getContainer().getNotificationsService().getPublicKey();
                if (publicKey) {
                    localStorage.setItem("notificationKey", publicKey);
                }
            }
            setPublicKey(publicKey);
        } catch (error) {
            console.error('Error fetching public key:', error);
        }
    }

    useEffect(() => {
        const initializePushNotificationStatus = async () => {
            try {
                await fetchAndSetPublicKey();

                // Check the service worker registration and subscription
                const swRegistration = await navigator.serviceWorker.ready;
                const subscription = await swRegistration.pushManager.getSubscription();

                // Check the permission state
                const permissionState = await swRegistration.pushManager.permissionState({userVisibleOnly: true});

                // If denied, set the blockedByChrome flag
                if (permissionState === 'denied') {
                    setBlockedByChrome(true);
                } else if (permissionState === 'granted') {
                    // If granted, check the subscription status
                    const isDeviceSubscribing = await getContainer().getNotificationsService().getSubscription(subscription?.endpoint || '');
                    setIsDeviceSubscribed(isDeviceSubscribing);
                } else {
                    // if prompt or default, set the subscription status to false
                    setIsDeviceSubscribed(false);
                }
                setDevicePermission(permissionState);
            } catch (error) {
                console.error('Error during service worker subscription flow:', error);
            }
        };

        initializePushNotificationStatus();
    }, []);

    return (
        <>
            <div className="row account-page-row">
                <div className={"col-md-6"}>
                    <label>{t("profile.settings.notifications-title")}:</label>
                    <span className={"help"}>{t("profile.settings.notifications-help")}</span>
                </div>
                <div className={"col-md-6 right"}>
                    <Checkbox checked={isDeviceSubscribed}
                              onChange={(e) => handleOnChange()}
                              disabled={blockedByChrome}/>
                    {blockedByChrome &&
                        <p className={"helpText"}>{t("profile.settings.notifications-blocked-label")}</p>}
                </div>
            </div>

        </>
    );
}

export const Checkbox = (props : { checked?: boolean, onChange? : (e?: ChangeEvent<HTMLInputElement>)=>void, disabled? : boolean}) => (
    <div className={"switch"}>
        <input type="checkbox" id="switch" className={"switch__input"} checked={props.checked} onChange={props.onChange} disabled={props.disabled}/>
        <label htmlFor="switch" className={"switch__label"}>
        </label>
    </div>
)
export const BonusCheckbox = (props : { checked?: boolean, onChange? : (checked : boolean)=>void, disabled? : boolean}) => {
    const handleChange = () => {
        props.onChange?.(!props.checked);
    }
    return (
        <div className={"switch"}>
            <input type="checkbox" id="switch" className={"switch__input"} defaultChecked={props.checked} onChange={handleChange} disabled={props.disabled}/>
            <label htmlFor="switch" className={"switch__label"}>
            </label>
        </div>
    )

}

const mapStateToProps = ({user, settings} : AppState) => {
    return {
        user : user.user,
        pushNotificationsEnabled : settings.settings && settings.settings.pushNotificationsEnabled
    }
}

const mapDispatchToProps = (dispatch : Dispatch) => (
    {

    }
)
export default connect(mapStateToProps, mapDispatchToProps)(NotificationsComponent);